function parseDate(val) {
  if (val == null || val === "") return 0;
  const s = String(val).trim();
  const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
  }
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getJoinTimestamp(employee) {
  return parseDate(
    employee?.dateOfJoin || employee?.joiningDate || employee?.joinDate
  );
}

function getExitTimestamp(employee) {
  const raw =
    employee?.relievedDate ||
    employee?.dateOfLeaving ||
    employee?.resignationDate ||
    employee?.terminationDate ||
    employee?.lastWorkingDay;
  return parseDate(raw);
}

function normalizeGender(employee) {
  const g = String(employee?.user?.gender || employee?.gender || "")
    .trim()
    .toLowerCase();
  if (g === "m" || g === "male" || g === "man") return "male";
  if (g === "f" || g === "female" || g === "woman" || g === "women") return "female";
  return "other";
}

function isInactiveStatus(employee) {
  const s = String(employee?.employmentStatus || "").trim().toLowerCase();
  return ["relieved", "terminated", "resigned"].includes(s);
}

export function getPeriodBounds(periodMode, { month, year, rangeFrom, rangeTo }) {
  const now = new Date();
  const y = Number(year) || now.getFullYear();
  const m = Number(month) || now.getMonth() + 1;

  if (periodMode === "year") {
    return {
      start: new Date(y, 0, 1).getTime(),
      end: new Date(y, 11, 31, 23, 59, 59, 999).getTime(),
      label: `Year ${y}`,
    };
  }

  if (periodMode === "range") {
    const start = parseDate(rangeFrom);
    const endRaw = parseDate(rangeTo);
    const end = endRaw ? endRaw + 86400000 - 1 : start;
    return { start, end, label: "Custom range" };
  }

  const start = new Date(y, m - 1, 1).getTime();
  const end = new Date(y, m, 0, 23, 59, 59, 999).getTime();
  const monthName = new Date(y, m - 1, 1).toLocaleString("default", {
    month: "long",
  });
  return { start, end, label: `${monthName} ${y}` };
}

export function computeEmployeeDashboardMetrics(employees, periodOptions) {
  const rows = Array.isArray(employees) ? employees : [];
  const { start, end, label } = getPeriodBounds(
    periodOptions.periodMode,
    periodOptions
  );

  let male = 0;
  let female = 0;
  let otherGender = 0;
  let active = 0;
  let probation = 0;
  let confirmed = 0;
  let relieved = 0;
  let resigned = 0;
  let terminated = 0;
  let newJoinees = 0;
  let attrition = 0;
  let fullTime = 0;
  let external = 0;

  for (const emp of rows) {
    const gender = normalizeGender(emp);
    if (gender === "male") male += 1;
    else if (gender === "female") female += 1;
    else otherGender += 1;

    const status = String(emp?.employmentStatus || "").trim().toLowerCase();
    if (status === "probation") probation += 1;
    else if (status === "confirmed") confirmed += 1;
    else if (status === "relieved") relieved += 1;
    else if (status === "resigned") resigned += 1;
    else if (status === "terminated") terminated += 1;

    if (!isInactiveStatus(emp)) active += 1;

    const empType = String(emp?.employmentType || "").trim().toLowerCase();
    if (empType === "external") external += 1;
    else if (empType) fullTime += 1;

    const joinTs = getJoinTimestamp(emp);
    if (joinTs && joinTs >= start && joinTs <= end) newJoinees += 1;

    const exitTs = getExitTimestamp(emp);
    if (exitTs && exitTs >= start && exitTs <= end && isInactiveStatus(emp)) {
      attrition += 1;
    }
  }

  const headcount = rows.length;
  const genderKnown = male + female;
  const malePct = genderKnown ? Math.round((male / genderKnown) * 100) : 0;
  const femalePct = genderKnown ? Math.round((female / genderKnown) * 100) : 0;
  const avgBase = active > 0 ? active : headcount;
  const newJoineeRate = avgBase ? ((newJoinees / avgBase) * 100).toFixed(1) : "0.0";
  const attritionRate = avgBase ? ((attrition / avgBase) * 100).toFixed(1) : "0.0";

  return {
    periodLabel: label,
    headcount,
    active,
    male,
    female,
    otherGender,
    malePct,
    femalePct,
    genderRatio:
      female > 0 ? (male / female).toFixed(2) : male > 0 ? "—" : "0",
    newJoinees,
    newJoineeRate,
    attrition,
    attritionRate,
    probation,
    confirmed,
    relieved,
    resigned,
    terminated,
    fullTime,
    external,
  };
}

function empDisplayName(employee) {
  const u = employee?.user || {};
  const parts = [u.name || u.firstName, u.lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return employee?.empCode || employee?.name || `Employee ${employee?.id ?? ""}`;
}

function resolveMasterLabel(employee, groupBy) {
  if (groupBy === "branch") {
    return (
      employee?.branch?.name ||
      employee?.branchName ||
      employee?.Branch?.name ||
      "Unassigned"
    );
  }
  if (groupBy === "designation") {
    return (
      employee?.designation?.name ||
      employee?.designationName ||
      employee?.Designation?.name ||
      "Unassigned"
    );
  }
  return (
    employee?.department?.name ||
    employee?.departmentName ||
    employee?.Department?.name ||
    "Unassigned"
  );
}

/** Headcount buckets for dashboard master charts (department / branch / designation). */
export function computeEmployeeMasterBuckets(employees, groupBy = "department") {
  const counts = {};
  for (const emp of Array.isArray(employees) ? employees : []) {
    if (isInactiveStatus(emp)) continue;
    const label = resolveMasterLabel(emp, groupBy);
    counts[label] = (counts[label] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

/** Decision alerts from employee master data (client-side). */
export function computeEmployeeDashboardAlerts(employees) {
  const rows = Array.isArray(employees) ? employees : [];
  const alerts = [];
  let missingDoj = 0;
  let missingDept = 0;
  let missingBranch = 0;
  const samples = { doj: [], dept: [], branch: [] };

  for (const emp of rows) {
    if (isInactiveStatus(emp)) continue;
    const name = empDisplayName(emp);
    if (!getJoinTimestamp(emp)) {
      missingDoj += 1;
      if (samples.doj.length < 5) samples.doj.push(name);
    }
    const dept = resolveMasterLabel(emp, "department");
    if (dept === "Unassigned") {
      missingDept += 1;
      if (samples.dept.length < 5) samples.dept.push(name);
    }
    const branch = resolveMasterLabel(emp, "branch");
    if (branch === "Unassigned") {
      missingBranch += 1;
      if (samples.branch.length < 5) samples.branch.push(name);
    }
  }

  if (missingDoj > 0) {
    alerts.push({
      id: "missing_doj",
      tone: "warning",
      title: "Missing joining date",
      detail: `${missingDoj} active employee(s) without DOJ. e.g. ${samples.doj.join(", ")}`,
    });
  }
  if (missingDept > 0) {
    alerts.push({
      id: "missing_dept",
      tone: "warning",
      title: "Unassigned department",
      detail: `${missingDept} active employee(s). e.g. ${samples.dept.join(", ")}`,
    });
  }
  if (missingBranch > 0) {
    alerts.push({
      id: "missing_branch",
      tone: "info",
      title: "Unassigned branch",
      detail: `${missingBranch} active employee(s). e.g. ${samples.branch.join(", ")}`,
    });
  }
  return alerts;
}

/** Calendar-year join vs exit series for trend charts. */
export function computeEmployeeMonthlyMovement(employees, year) {
  const y = Number(year) || new Date().getFullYear();
  const joins = Array.from({ length: 12 }, () => 0);
  const exits = Array.from({ length: 12 }, () => 0);
  const labels = Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i, 1).toLocaleString("default", { month: "short" })
  );

  for (const emp of Array.isArray(employees) ? employees : []) {
    const joinTs = getJoinTimestamp(emp);
    if (joinTs) {
      const d = new Date(joinTs);
      if (d.getFullYear() === y) joins[d.getMonth()] += 1;
    }
    const exitTs = getExitTimestamp(emp);
    if (exitTs && isInactiveStatus(emp)) {
      const d = new Date(exitTs);
      if (d.getFullYear() === y) exits[d.getMonth()] += 1;
    }
  }

  return { year: y, labels, joins, exits };
}

/** Tenure buckets for active employees (decision mix). */
export function computeTenureBuckets(employees) {
  const now = Date.now();
  const buckets = [
    { key: "lt1", label: "< 1 year", value: 0 },
    { key: "y1_3", label: "1–3 years", value: 0 },
    { key: "y3_5", label: "3–5 years", value: 0 },
    { key: "gt5", label: "5+ years", value: 0 },
    { key: "unknown", label: "Unknown DOJ", value: 0 },
  ];
  for (const emp of Array.isArray(employees) ? employees : []) {
    if (isInactiveStatus(emp)) continue;
    const joinTs = getJoinTimestamp(emp);
    if (!joinTs) {
      buckets[4].value += 1;
      continue;
    }
    const years = (now - joinTs) / (365.25 * 86400000);
    if (years < 1) buckets[0].value += 1;
    else if (years < 3) buckets[1].value += 1;
    else if (years < 5) buckets[2].value += 1;
    else buckets[3].value += 1;
  }
  return buckets.filter((b) => b.value > 0 || b.key !== "unknown");
}

/**
 * Month snapshots for Compare (client-side from roster DOJ/LWD).
 * Active = joined on/before month-end and not exited on/before month-end.
 */
export function computeEmployeeCompareSeries(employees, payMonths = []) {
  const rows = Array.isArray(employees) ? employees : [];
  return (payMonths || []).map((ym) => {
    const [ys, ms] = String(ym).split("-").map(Number);
    const monthStart = new Date(ys, ms - 1, 1).getTime();
    const monthEnd = new Date(ys, ms, 0, 23, 59, 59, 999).getTime();
    let joins = 0;
    let exits = 0;
    let active = 0;
    let male = 0;
    let female = 0;
    let probation = 0;

    for (const emp of rows) {
      const joinTs = getJoinTimestamp(emp);
      const exitTs = getExitTimestamp(emp);
      if (joinTs && joinTs >= monthStart && joinTs <= monthEnd) joins += 1;
      if (exitTs && isInactiveStatus(emp) && exitTs >= monthStart && exitTs <= monthEnd) {
        exits += 1;
      }
      const onRollsEnd =
        joinTs > 0 && joinTs <= monthEnd && (!exitTs || exitTs > monthEnd);
      if (onRollsEnd) {
        active += 1;
        const g = normalizeGender(emp);
        if (g === "male") male += 1;
        else if (g === "female") female += 1;
        if (String(emp?.employmentStatus || "").toLowerCase() === "probation") {
          probation += 1;
        }
      }
    }

    return {
      payMonth: ym,
      label: ym,
      joins,
      exits,
      active,
      male,
      female,
      probation,
      netChange: joins - exits,
    };
  });
}

export function buildEmployeeCardDetail(cardKey, { metrics, tenure }) {
  const common = { periodLabel: metrics?.periodLabel || "", generatedAt: new Date().toISOString() };
  switch (cardKey) {
    case "headcount":
      return {
        title: "Headcount",
        summary: [
          ["Total listed", metrics.headcount],
          ["Active", metrics.active],
          ["Probation", metrics.probation],
          ["Confirmed", metrics.confirmed],
        ],
        note: "Active excludes relieved / resigned / terminated statuses.",
        ...common,
      };
    case "joins":
      return {
        title: "New joinees",
        summary: [
          ["Joins in period", metrics.newJoinees],
          ["Join rate %", metrics.newJoineeRate],
          ["Active base", metrics.active],
        ],
        note: "Joins counted by DOJ within the selected period.",
        ...common,
      };
    case "attrition":
      return {
        title: "Attrition",
        summary: [
          ["Exits in period", metrics.attrition],
          ["Attrition rate %", metrics.attritionRate],
          ["Relieved", metrics.relieved],
          ["Resigned", metrics.resigned],
          ["Terminated", metrics.terminated],
        ],
        note: "Exits counted by LWD / relieved date within period for inactive statuses.",
        ...common,
      };
    case "gender":
      return {
        title: "Gender mix",
        summary: [
          ["Men", metrics.male],
          ["Women", metrics.female],
          ["Other / unknown", metrics.otherGender],
          ["M:F ratio", metrics.genderRatio],
        ],
        ...common,
      };
    case "tenure":
      return {
        title: "Tenure mix (active)",
        summary: (tenure || []).map((t) => [t.label, t.value]),
        note: "Tenure from DOJ to today for active employees.",
        ...common,
      };
    default:
      return {
        title: cardKey || "Detail",
        summary: [["Info", "No mapping"]],
        ...common,
      };
  }
}
